import React, { useEffect, useMemo, useState } from 'react';
import { Chip, Paper, useMediaQuery } from '@mui/material';
import { useDispatch } from 'react-redux';
import { setLoader, setSnackBar, setSnackBarMessage, setSnackBarVariant } from '../../redux';
import ApiService from '../../_services/ApiService';
import TableComponent from '../importRevenue/Tablecomponent';
import SnackbarContainer from '../Snackbar';
import { default_limit } from '../../_services/_constants/recordLimitset.constant';
import { getDateAsPerFormat } from '../../_helpers/reusablefunctions/dateSetter';

// The API returns requested_date in two shapes: ISO ("2026-07-10" / with time)
// and DD-MM-YY ("23-07-26"). Return a valid Date, or null when unparseable.
function parseRequestedDate(value) {
  if (!value) return null;
  const ddmmyy = /^(\d{2})-(\d{2})-(\d{2})$/.exec(value);
  if (ddmmyy) {
    const [, dd, mm, yy] = ddmmyy;
    const d = new Date(2000 + Number(yy), Number(mm) - 1, Number(dd));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

// The three request buckets returned by the status-requests API, in display order.
const FILTERS = [
  { key: 'in_review', label: 'In Review', color: '#2196f3' },
  { key: 'redelivery_requested', label: 'Redelivery Requested', color: '#ff9800' },
  { key: 'takedown_requested', label: 'Takedown Requested', color: '#f44336' },
];

function StatusRequests() {
  const dispatch = useDispatch();
  const smScreen = useMediaQuery('(max-width:768px)');
  const [summary, setSummary] = useState({});
  const [data, setData] = useState({});
  const [activeFilter, setActiveFilter] = useState('in_review');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(default_limit);

  useEffect(() => {
    getStatusRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getStatusRequests() {
    dispatch(setLoader(true));
    ApiService.get('statusRequests').then((response) => {
      dispatch(setLoader(false));
      if (response && response.status === 200) {
        setSummary(response.data?.summary || {});
        setData(response.data || {});
      } else {
        showError();
      }
    }).catch((error) => {
      dispatch(setLoader(false));
      showError();
      return error?.response;
    });
  }

  function showError() {
    dispatch(setSnackBar(true));
    dispatch(setSnackBarMessage('Something went wrong'));
    dispatch(setSnackBarVariant('error'));
    setTimeout(() => dispatch(setSnackBar(false)), 3000);
  }

  const handleFilterChange = (key) => {
    setActiveFilter(key);
    setPage(0);
  };

  const activeRows = useMemo(() => data[activeFilter] || [], [data, activeFilter]);

  // The API returns every bucket inline, so paginate on the client.
  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return activeRows.slice(start, start + rowsPerPage);
  }, [activeRows, page, rowsPerPage]);

  const columns = [
    {
      id: 'type', label: 'Type', template: (row) => (
        <Chip
          size="small"
          label={row.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1) : '--'}
          sx={{ background: row.type === 'album' ? '#673ab7' : '#009688', color: '#fff' }}
        />
      ),
    },
    { id: 'name', field: 'name', label: 'Name', template: (row) => row.name || '--' },
    { id: 'studio_name', field: 'studio_name', label: 'Studio Name', template: (row) => row.studio_name || '--' },
    {
      id: 'requested_date', label: 'Requested Date', template: (row) => {
        // requested_date may be null or an unparseable value — show blank in that case.
        const date = parseRequestedDate(row.requested_date);
        return date ? getDateAsPerFormat(date, 'DD Mon, YYYY') : '--';
      },
    },
  ];

  return (
    <div className={`row ${smScreen ? 'p-2' : ''}`} style={{ padding: '1rem 1rem 3rem 1rem' }}>
      <h2 style={{ marginLeft: '10px' }}>Status Requests</h2>

      <div className="d-flex gap-2 align-items-center flex-wrap mt-2" style={{ marginLeft: '10px' }}>
        {FILTERS.map((filter) => {
          const isActive = activeFilter === filter.key;
          const count = summary[filter.key] ?? (data[filter.key]?.length || 0);
          return (
            <Chip
              key={filter.key}
              label={`${filter.label} (${count})`}
              onClick={() => handleFilterChange(filter.key)}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                cursor: 'pointer',
                fontWeight: isActive ? 600 : 400,
                background: isActive ? filter.color : 'transparent',
                color: isActive ? '#fff' : filter.color,
                borderColor: filter.color,
                '&:hover': { background: isActive ? filter.color : `${filter.color}22` },
              }}
            />
          );
        })}
      </div>

      <div>
        <div className="container-fluid py-4 creator-playlist-panel">
          <Paper className="shadow-table" sx={{ width: '100%', mb: '3rem' }}>
            <TableComponent
              columns={columns}
              data={pagedRows}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              totalCount={activeRows.length}
              removeStyle={'removeStyle'}
            />
          </Paper>
        </div>
      </div>
      <SnackbarContainer />
    </div>
  );
}

export default StatusRequests;
